import { useState, useLayoutEffect } from 'react';

/**
 * 손님 주문 화면의 레이아웃 모드 판정.
 *
 * - 'mobile'  : 기존 360px 아트보드 UI (QR 손님 전부)
 * - 'tablet'  : 테이블오더(태블릿) 매장 전용 UI
 *
 * 판정 기준은 "테이블오더 매장인가" 하나뿐이다. 화면 크기·방향은 보지 않는다.
 * 예전에는 가로 1024px 이상이면 태블릿 UI 를 띄웠는데, 그러면
 *   - 태블릿 매장인데 화면이 세로거나 작으면 모바일 UI 가 뜨고
 *   - 태블릿을 도입하지 않은 매장의 QR 손님이 아이패드 가로로 들어오면 태블릿 UI 가 떴다
 * 어느 쪽도 의도가 아니다. 태블릿 UI 는 마스터에서 지정한 매장에서만 뜬다.
 *
 * @param {boolean} enabled 테이블오더(태블릿) 경로 여부
 */
const OVERRIDE_KEY = 'layoutOverride';

// ?layout=tablet|mobile|auto 오버라이드(테스트용)를 "모듈 로드 시점"에 캡처한다.
// orderPage 의 verifySession 이 history.replaceState 로 쿼리스트링을 통째로 지우기 때문에
// 컴포넌트 이펙트에서 읽으면 이미 사라진 뒤다.
(function captureLayoutOverride() {
  if (typeof window === 'undefined') return;
  try {
    const value = new URLSearchParams(window.location.search).get('layout');
    if (value === 'tablet' || value === 'mobile') {
      sessionStorage.setItem(OVERRIDE_KEY, value);
    } else if (value === 'auto') {
      sessionStorage.removeItem(OVERRIDE_KEY);
    }
  } catch (_) {
    // 사파리 프라이빗 모드 등에서 sessionStorage 접근 불가 — 오버라이드 없음으로 진행
  }
})();

const getOverride = () => {
  try {
    return sessionStorage.getItem(OVERRIDE_KEY);
  } catch (_) {
    return null;
  }
};

// ?layout=tablet 강제 지정 여부. 태블릿 UI 는 테이블오더 경로에서만 뜨는데,
// 디자인 확인은 그 경로 밖에서도 해야 하므로 호출부가 이 값을 함께 본다.
export const isTabletForced = () => getOverride() === 'tablet';

export default function useLayoutMode(enabled) {
  const [override] = useState(getOverride);
  const mode = (override === 'tablet' || override === 'mobile')
    ? override
    : (enabled ? 'tablet' : 'mobile');
  const isTablet = mode === 'tablet';

  // 태블릿에서 기존 바텀시트들의 zoom 을 눌러주기 위한 훅 (TabletOrderView 의 스타일이 이 클래스를 씀)
  useLayoutEffect(() => {
    if (typeof document === 'undefined') return undefined;
    document.body.classList.toggle('tablet-layout', isTablet);
    return () => {
      document.body.classList.remove('tablet-layout');
    };
  }, [isTablet]);

  return { mode, isTablet };
}
